import React from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'primary' | 'brand' | 'gray' | 'outline' | 'secondary' | 'blue' | 'green' | 'yellow' | 'red' | 'orange' | 'purple';
  size?: 'sm' | 'md';
  dot?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot = false, children, ...props }, ref) => {
    // Variant names are all preserved for backward compatibility; duplicate
    // hues are aliased to shared semantic tokens so badges converge visually
    // (one gray, one green, etc.) while every existing call site keeps working.
    //
    // `primary` is a STATUS tone and wears navy, not the ember brand: some
    // forty status maps use it as the cool "in progress" chip beside amber
    // and orange ones, where ember would be indistinguishable. `brand` is
    // the ember chip, for when the brand itself is what is being marked.
    const variants = {
      default: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-500/15',
      success: 'bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-300 ring-success-600/20',
      warning: 'bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 ring-warning-600/20',
      danger: 'bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 ring-danger-600/20',
      primary: 'bg-navy-50 dark:bg-navy-900/40 text-navy-700 dark:text-navy-200 ring-navy-700/15',
      brand: 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-primary-600/20',
      gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-500/15', // alias → default
      outline: 'bg-transparent text-gray-700 dark:text-gray-300 ring-gray-300 dark:ring-gray-700',
      secondary: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-500/15', // alias → default
      blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-600/20',
      green: 'bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-300 ring-success-600/20', // alias → success
      yellow: 'bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 ring-warning-600/20', // alias → warning
      red: 'bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 ring-danger-600/20', // alias → danger
      orange: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 ring-orange-600/20',
      purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-purple-600/20',
    };

    const sizes = {
      sm: 'px-2 py-0.5 text-[11px]',
      md: 'px-2.5 py-0.5 text-xs',
    };

    const dotColors = {
      default: 'bg-gray-500',
      success: 'bg-success-500',
      warning: 'bg-warning-500',
      danger: 'bg-danger-500',
      primary: 'bg-navy-500',
      brand: 'bg-primary-500',
      gray: 'bg-gray-400',
      outline: 'bg-gray-500',
      secondary: 'bg-gray-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      purple: 'bg-purple-500',
    };

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full font-medium ring-1 ring-inset',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {dot && <span className={cn('w-1.5 h-1.5 rounded-full', dotColors[variant])} />}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
